import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Observable, Subject, catchError, tap, throwError, timeout } from 'rxjs';
import { ConfigService } from './config.service';
import { AuthService } from './auth.service';
import { I18nService } from '../i18n/i18n.service';

// ─── Types ──────────────────────────────────────────────────────────────
export type AssistantRole = 'user' | 'assistant' | 'system' | 'tool';

export interface AssistantThread {
  id: string;
  vendor_id: number;
  user_id: number;
  title: string | null;
  message_count: number;
  last_message_at: string | null;
  provider_snapshot: string | null;
  model_snapshot: string | null;
  archived: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface AssistantMessageContent {
  text?: string;
  [k: string]: unknown;
}

export interface AssistantToolCall {
  id?: string;
  name: string;
  input?: unknown;
}

export interface AssistantToolResult {
  name: string;
  output?: unknown;
}

export interface AssistantMessage {
  id: string;
  thread_id: string;
  role: AssistantRole;
  content: AssistantMessageContent;
  tool_calls?: AssistantToolCall[] | null;
  tool_results?: AssistantToolResult[] | null;
  model_used?: string | null;
  provider_used?: string | null;
  latency_ms?: number | null;
  tokens_in?: number | null;
  tokens_out?: number | null;
  error?: string | null;
  user_id?: number | null;
  created_at: string | null;
}

export interface AssistantConfig {
  provider: string;
  model: string;
  max_tokens: number;
  temperature: number;
  rate_limit_per_user_hour: number;
  rate_limit_per_vendor_hour: number;
  shim_idle_timeout_min: number;
  shim_max_concurrent_sessions: number;
  daily_message_cap: number;
  enabled: boolean;
  source: 'global_default' | 'vendor_override' | 'vendor_disabled';
}

export interface AssistantConfigResponse {
  vendor_id: number;
  config: AssistantConfig;
}

export interface AssistantHealth {
  vendor_id: number;
  orchestrator: { status?: string; detail?: string; [k: string]: unknown } | null;
  shim: { status?: string; detail?: string; [k: string]: unknown } | null;
}

// ─── Admin types ────────────────────────────────────────────────────────
export interface VendorAIOverride {
  vendor_id: number;
  provider_override: string | null;
  model_override: string | null;
  enabled: boolean;
  rate_limit_per_user_hour: number | null;
  rate_limit_per_vendor_hour: number | null;
  shim_idle_timeout_min: number | null;
  shim_max_concurrent_sessions: number | null;
  daily_message_cap: number | null;
  updated_at: string | null;
  updated_by: number | null;
}

export interface CreditBalanceRow {
  vendor_id: number;
  balance: number;
  last_activity?: string | null;
}

export interface CreditLedgerEntry {
  id: number;
  delta: number;
  kind: 'grant' | 'consume' | 'refund' | 'adjust';
  feature?: string | null;
  generation_id?: number | null;
  note?: string | null;
  created_by?: number | null;
  created_at?: string | null;
}

export interface AiUsageRow {
  vendor_id: number;
  kind: string;
  provider: string;
  count: number;
  credits: number;
}

export interface AtlasProviderStatus {
  configured: boolean;
  base_url: string;
  mode: 'auto' | 'manual';
  text_model: string;
  image_model: string;
  video_model: string;
}

export interface AdminCreditsOverview {
  balances: CreditBalanceRow[];
  pricing: Record<string, number>;
  premium_provider: AtlasProviderStatus;
  usage: AiUsageRow[];
}

export interface AtlasCatalogEntry {
  id: string;
  label: string;
  price_usd: number | null;
  profile?: string;
  validated_coding_plan?: boolean;
}

export interface AtlasModelConfig {
  mode: 'auto' | 'manual';
  text_model: string | null;
  image_model: string | null;
  video_model: string | null;
}

export interface AdminAtlasConfig {
  config: AtlasModelConfig;
  status: AtlasProviderStatus;
  auto_text_candidates: string[];
  catalog: {
    text: AtlasCatalogEntry[];
    image: AtlasCatalogEntry[];
    video: AtlasCatalogEntry[];
  };
}

/** Carte de routage besoin→modèles : kind → profile → tier → [ids]. */
export type AtlasRoutingMap = Record<
  string,
  Record<string, Record<string, string[]>>
>;

/** Réponse GET /atlas/routing : curation experte de l'atelier IA. */
export interface AdminAtlasRouting {
  routing: AtlasRoutingMap;
  defaults: AtlasRoutingMap;
  upscale_models: Record<string, string[]>;
  tiers: string[];
  catalog: {
    image?: AtlasCatalogEntry[];
    image_edit?: AtlasCatalogEntry[];
    video?: AtlasCatalogEntry[];
    image_to_video?: AtlasCatalogEntry[];
    [k: string]: AtlasCatalogEntry[] | undefined;
  };
  pricing: Record<string, number>;
  pricing_overrides: Record<string, number>;
}

export interface VendorAISettingsPayload {
  provider_override?: string | null;
  model_override?: string | null;
  max_tokens_override?: number | null;
  temperature_override?: number | null;
  rate_limit_per_user_hour?: number | null;
  rate_limit_per_vendor_hour?: number | null;
  shim_idle_timeout_min?: number | null;
  shim_max_concurrent_sessions?: number | null;
  daily_message_cap?: number | null;
  enabled?: boolean | null;
}

export interface ShimInfo {
  vendor_id?: number | string;
  service?: string;
  replicas?: number;
  desired_replicas?: number;
  status?: string;
  last_activity?: string | null;
  workspace_path?: string;
  ram_mb?: number;
  [k: string]: unknown;
}

export interface SecurityOverview {
  orchestrator: { status?: string; version?: string; uptime?: number | string; detail?: string; [k: string]: unknown } | null;
  shims: ShimInfo[];
  workspaces: number[];
  errors: string[];
  premium_provider?: AtlasProviderStatus;
}

// ─── SSE event shape ────────────────────────────────────────────────────
export type AssistantStreamEventType =
  | 'meta'
  | 'token'
  | 'agent_step'
  | 'tool_call'
  | 'tool_result'
  | 'done'
  | 'error';

export interface AssistantStreamEvent {
  type: AssistantStreamEventType;
  data: any;
}

/**
 * AssistantService — gestion globale de la bulle IA (Sprint 3).
 *
 * Pattern : signals (myThreads, currentMessages, config, health) + HttpClient
 * pour le CRUD, fetch+ReadableStream pour le streaming SSE (HttpClient ne
 * supporte pas nativement les flux text/event-stream).
 *
 * Tous les états mutables sont exposés en signals read-only ; les composants
 * consomment via `threads()`, `config()`, etc.
 */
@Injectable({ providedIn: 'root' })
export class AssistantService {
  private readonly http = inject(HttpClient);
  private readonly cookies = inject(CookieService);
  private readonly cfg = inject(ConfigService);
  private readonly auth = inject(AuthService);
  private readonly i18n = inject(I18nService);

  private readonly base = `${this.cfg.apiUrl}/assistant`;
  private readonly adminBase = `${this.cfg.apiUrl}/admin/assistant`;

  // ── State signals ──────────────────────────────────────────────────
  readonly threads = signal<AssistantThread[]>([]);
  readonly currentThreadId = signal<string | null>(null);
  readonly currentMessages = signal<AssistantMessage[]>([]);
  readonly loading = signal<boolean>(false);
  readonly streaming = signal<boolean>(false);
  readonly error = signal<string | null>(null);
  readonly config = signal<AssistantConfig | null>(null);
  readonly vendorId = signal<number | null>(null);
  readonly health = signal<AssistantHealth | null>(null);
  /** Compteur de messages envoyés aujourd'hui (rafraîchi à chaque envoi local). */
  readonly dailyCount = signal<number>(0);

  readonly currentThread = computed<AssistantThread | null>(() => {
    const id = this.currentThreadId();
    if (!id) return null;
    return this.threads().find((t) => t.id === id) || null;
  });

  // ── HTTP helpers ───────────────────────────────────────────────────
  private authHeaders(extra: Record<string, string> = {}): HttpHeaders {
    const token = this.cookies.get('access_token') || this.auth.getTokenFromCookies() || '';
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      ...extra,
    });
  }

  private jsonHeaders(): HttpHeaders {
    return this.authHeaders({ 'Content-Type': 'application/json' });
  }

  private withErrors<T>(label: string) {
    return (source$: Observable<T>): Observable<T> =>
      source$.pipe(
        timeout(15000),
        catchError((err) => {
          let message = `Erreur ${label}.`;
          if (err?.name === 'TimeoutError') {
            message = `Le serveur met trop de temps à répondre (${label}).`;
          } else if (err?.status === 401 || err?.status === 403) {
            message = err?.error?.detail || 'Accès refusé. Vérifiez vos permissions.';
          } else if (err?.status === 404) {
            message = `Ressource introuvable (${label}).`;
          } else if (err?.status === 429) {
            message =
              err?.error?.detail || 'Quota atteint. Réessayez dans quelques minutes.';
          } else if (err?.status === 503) {
            message =
              err?.error?.detail || 'Service IA indisponible. Réessayez dans un instant.';
          } else if (err?.error?.detail) {
            message = String(err.error.detail);
          }
          return throwError(() => ({ ...err, customMessage: message }));
        }),
      );
  }

  // ─── Threads CRUD ─────────────────────────────────────────────────
  listThreads(includeArchived = false, limit = 50): Observable<AssistantThread[]> {
    const params = new URLSearchParams();
    params.set('include_archived', String(includeArchived));
    params.set('limit', String(limit));
    return this.http
      .get<AssistantThread[]>(`${this.base}/threads/?${params.toString()}`, {
        headers: this.authHeaders(),
      })
      .pipe(
        tap((rows) => this.threads.set(rows || [])),
        this.withErrors('chargement des conversations'),
      );
  }

  createThread(title?: string): Observable<AssistantThread> {
    return this.http
      .post<AssistantThread>(
        `${this.base}/threads/`,
        title ? { title } : {},
        { headers: this.jsonHeaders() },
      )
      .pipe(
        tap((thread) => {
          this.threads.update((prev) => [thread, ...prev.filter((t) => t.id !== thread.id)]);
          this.currentThreadId.set(thread.id);
          this.currentMessages.set([]);
        }),
        this.withErrors("création d'une conversation"),
      );
  }

  getThread(id: string): Observable<{ thread: AssistantThread; messages: AssistantMessage[] }> {
    return this.http
      .get<{ thread: AssistantThread; messages: AssistantMessage[] }>(
        `${this.base}/threads/${encodeURIComponent(id)}`,
        { headers: this.authHeaders() },
      )
      .pipe(
        tap((resp) => {
          if (resp?.thread) {
            this.threads.update((prev) => {
              const exists = prev.some((t) => t.id === resp.thread.id);
              return exists
                ? prev.map((t) => (t.id === resp.thread.id ? resp.thread : t))
                : [resp.thread, ...prev];
            });
            this.currentThreadId.set(resp.thread.id);
          }
          this.currentMessages.set(resp?.messages || []);
        }),
        this.withErrors('chargement du thread'),
      );
  }

  deleteThread(id: string): Observable<void> {
    return this.http
      .delete<void>(`${this.base}/threads/${encodeURIComponent(id)}`, {
        headers: this.authHeaders(),
      })
      .pipe(
        tap(() => {
          this.threads.update((prev) => prev.filter((t) => t.id !== id));
          if (this.currentThreadId() === id) {
            this.currentThreadId.set(null);
            this.currentMessages.set([]);
          }
        }),
        this.withErrors('suppression du thread'),
      );
  }

  archiveThread(id: string): Observable<void> {
    return this.http
      .post<void>(`${this.base}/threads/${encodeURIComponent(id)}/archive`, {}, {
        headers: this.jsonHeaders(),
      })
      .pipe(
        tap(() => {
          this.threads.update((prev) =>
            prev.map((t) => (t.id === id ? { ...t, archived: true } : t)),
          );
          if (this.currentThreadId() === id) {
            this.currentThreadId.set(null);
            this.currentMessages.set([]);
          }
        }),
        this.withErrors("archivage du thread"),
      );
  }

  // ─── Config + health ──────────────────────────────────────────────
  getConfig(): Observable<AssistantConfigResponse> {
    return this.http
      .get<AssistantConfigResponse>(`${this.base}/config`, {
        headers: this.authHeaders(),
      })
      .pipe(
        tap((resp) => {
          this.config.set(resp?.config || null);
          this.vendorId.set(resp?.vendor_id ?? null);
        }),
        this.withErrors('chargement de la configuration IA'),
      );
  }

  getHealth(): Observable<AssistantHealth> {
    return this.http
      .get<AssistantHealth>(`${this.base}/health`, { headers: this.authHeaders() })
      .pipe(
        tap((resp) => this.health.set(resp || null)),
        this.withErrors('chargement de la santé du shim IA'),
      );
  }

  // ─── SSE streaming chat ───────────────────────────────────────────
  /**
   * Streame /assistant/chat (SSE). Émet sur un Subject typé chaque event
   * parsé (`meta`, `token`, `tool_call`, `tool_result`, `done`, `error`).
   *
   * Retourne :
   *  - `events$` : Subject à souscrire (un seul abonné suffit)
   *  - `close`   : abort de la connexion (timeout, navigation, etc.)
   *
   * Pourquoi `fetch` + ReadableStream plutôt que `EventSource` :
   * EventSource ne supporte ni POST ni l'en-tête Authorization. On parse
   * donc le format SSE manuellement (frames séparées par `\n\n`).
   */
  streamChat(
    threadId: string | null,
    message: string,
  ): { events$: Subject<AssistantStreamEvent>; close: () => void } {
    const events$ = new Subject<AssistantStreamEvent>();
    const controller = new AbortController();

    // Watchdog : 200s max pour éviter de garder une connexion zombie. Aligné
    // sur un tour agentique Opus 4.8 + MCP + cold-start shim (peut dépasser
    // 120s) ; cf. site-builder.service.ts.
    const watchdog = setTimeout(() => {
      try { controller.abort(); } catch {}
      events$.next({
        type: 'error',
        data: { error: 'timeout', detail: 'Stream SSE coupé après 200s.' },
      });
      events$.complete();
    }, 200_000);

    const url = `${this.base}/chat`;
    const token = this.cookies.get('access_token') || this.auth.getTokenFromCookies() || '';

    this.streaming.set(true);
    this.error.set(null);

    void (async () => {
      try {
        const resp = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            thread_id: threadId,
            message,
            locale: this.i18n.currentLocale(),
          }),
          signal: controller.signal,
        });
        if (!resp.ok || !resp.body) {
          let detail = `HTTP ${resp.status}`;
          try {
            const txt = await resp.text();
            if (txt) detail = txt.slice(0, 500);
          } catch { /* noop */ }
          events$.next({ type: 'error', data: { error: 'http_error', detail } });
          events$.complete();
          return;
        }
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          // Normalise CRLF→LF : sse-starlette sépare ses frames par `\r\n\r\n`,
          // or on découpe sur `\n\n`. Sans normalisation, `indexOf('\n\n')` ne
          // matche jamais → aucun event parsé → spinner infini. Robuste aussi
          // si un `\r\n` est coupé entre deux chunks réseau.
          buffer = (buffer + decoder.decode(value, { stream: true })).replace(
            /\r\n/g,
            '\n',
          );
          let idx: number;
          while ((idx = buffer.indexOf('\n\n')) !== -1) {
            const frame = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            const ev = this.parseSseFrame(frame);
            if (ev) events$.next(ev);
          }
        }
        // Drain final partial frame éventuel.
        if (buffer.trim()) {
          const ev = this.parseSseFrame(buffer);
          if (ev) events$.next(ev);
        }
        events$.complete();
      } catch (e: any) {
        if (e?.name === 'AbortError') {
          events$.complete();
          return;
        }
        events$.next({
          type: 'error',
          data: { error: 'fetch_failed', detail: String(e?.message || e) },
        });
        events$.complete();
      } finally {
        clearTimeout(watchdog);
        this.streaming.set(false);
      }
    })();

    return {
      events$,
      close: () => {
        clearTimeout(watchdog);
        try { controller.abort(); } catch {}
        this.streaming.set(false);
      },
    };
  }

  private parseSseFrame(frame: string): AssistantStreamEvent | null {
    let eventType: AssistantStreamEventType = 'token';
    let dataLine = '';
    for (const line of frame.split('\n')) {
      if (line.startsWith('event:')) {
        const t = line.slice(6).trim();
        if (
          t === 'meta' || t === 'token' || t === 'agent_step' ||
          t === 'tool_call' || t === 'tool_result' || t === 'done' || t === 'error'
        ) {
          eventType = t;
        }
      } else if (line.startsWith('data:')) {
        dataLine += line.slice(5).trim();
      }
    }
    if (!dataLine) return null;
    try {
      return { type: eventType, data: JSON.parse(dataLine) };
    } catch {
      return { type: eventType, data: dataLine };
    }
  }

  // ─── Local state helpers (pour le composant bulle) ───────────────
  appendMessageLocally(msg: AssistantMessage): void {
    this.currentMessages.update((prev) => [...prev, msg]);
  }

  replaceLastAssistant(msg: AssistantMessage): void {
    this.currentMessages.update((prev) => {
      const out = [...prev];
      for (let i = out.length - 1; i >= 0; i--) {
        if (out[i].role === 'assistant') {
          out[i] = msg;
          return out;
        }
      }
      out.push(msg);
      return out;
    });
  }

  bumpDailyCount(): void {
    this.dailyCount.update((n) => n + 1);
  }

  resetCurrentThread(): void {
    this.currentThreadId.set(null);
    this.currentMessages.set([]);
  }

  // ═══════════════════════════ Admin API ══════════════════════════════

  adminListVendorOverrides(): Observable<VendorAIOverride[]> {
    return this.http
      .get<VendorAIOverride[]>(`${this.adminBase}/vendor-ai-settings/`, {
        headers: this.authHeaders(),
      })
      .pipe(this.withErrors('chargement des overrides vendor'));
  }

  adminGetVendorSettings(vendorId: number): Observable<AssistantConfig> {
    return this.http
      .get<AssistantConfig>(
        `${this.adminBase}/vendor-ai-settings/${vendorId}`,
        { headers: this.authHeaders() },
      )
      .pipe(this.withErrors('chargement des paramètres vendor'));
  }

  adminUpsertVendorSettings(
    vendorId: number,
    payload: VendorAISettingsPayload,
  ): Observable<AssistantConfig> {
    return this.http
      .put<AssistantConfig>(
        `${this.adminBase}/vendor-ai-settings/${vendorId}`,
        payload,
        { headers: this.jsonHeaders() },
      )
      .pipe(this.withErrors("enregistrement de l'override vendor"));
  }

  adminDeleteVendorSettings(vendorId: number): Observable<void> {
    return this.http
      .delete<void>(`${this.adminBase}/vendor-ai-settings/${vendorId}`, {
        headers: this.authHeaders(),
      })
      .pipe(this.withErrors("suppression de l'override vendor"));
  }

  // ── Crédits IA (premium AtlasCloud) ───────────────────────────────
  adminCreditsOverview(): Observable<AdminCreditsOverview> {
    return this.http
      .get<AdminCreditsOverview>(`${this.adminBase}/credits/`, {
        headers: this.authHeaders(),
      })
      .pipe(this.withErrors('chargement des crédits'));
  }

  adminGrantCredits(
    vendorId: number,
    amount: number,
    note?: string,
  ): Observable<{ vendor_id: number; balance: number }> {
    return this.http
      .post<{ vendor_id: number; balance: number }>(
        `${this.adminBase}/credits/grant`,
        { vendor_id: vendorId, amount, note },
        { headers: this.jsonHeaders() },
      )
      .pipe(this.withErrors('attribution de crédits'));
  }

  adminCreditsLedger(
    vendorId: number,
    limit = 50,
  ): Observable<{ vendor_id: number; balance: number; ledger: CreditLedgerEntry[] }> {
    return this.http
      .get<{ vendor_id: number; balance: number; ledger: CreditLedgerEntry[] }>(
        `${this.adminBase}/credits/${vendorId}/ledger?limit=${limit}`,
        { headers: this.authHeaders() },
      )
      .pipe(this.withErrors('chargement du grand livre crédits'));
  }

  // ── Modèles AtlasCloud (choix dynamique via UI) ───────────────────
  adminAtlasConfig(): Observable<AdminAtlasConfig> {
    return this.http
      .get<AdminAtlasConfig>(`${this.adminBase}/atlas/config`, {
        headers: this.authHeaders(),
      })
      .pipe(this.withErrors('chargement config AtlasCloud'));
  }

  adminAtlasConfigPatch(
    patch: Partial<AtlasModelConfig>,
  ): Observable<{ config: AtlasModelConfig; status: AtlasProviderStatus }> {
    return this.http
      .patch<{ config: AtlasModelConfig; status: AtlasProviderStatus }>(
        `${this.adminBase}/atlas/config`,
        patch,
        { headers: this.jsonHeaders() },
      )
      .pipe(this.withErrors('mise à jour config AtlasCloud'));
  }

  adminAtlasTest(
    model?: string,
  ): Observable<{ ok: boolean; model?: string; reason?: string; detail?: string }> {
    return this.http
      .post<{ ok: boolean; model?: string; reason?: string; detail?: string }>(
        `${this.adminBase}/atlas/test`,
        { model: model || null },
        { headers: this.jsonHeaders() },
      )
      .pipe(this.withErrors('test AtlasCloud'));
  }

  // ── Atelier IA : routage besoin→modèle + prix par tier ──
  adminAtlasRouting(): Observable<AdminAtlasRouting> {
    return this.http
      .get<AdminAtlasRouting>(`${this.adminBase}/atlas/routing`, {
        headers: this.authHeaders(),
      })
      .pipe(this.withErrors('chargement routage atelier IA'));
  }

  adminAtlasRoutingPatch(
    routing: AtlasRoutingMap,
  ): Observable<{ routing: AtlasRoutingMap }> {
    return this.http
      .patch<{ routing: AtlasRoutingMap }>(
        `${this.adminBase}/atlas/routing`,
        { routing },
        { headers: this.jsonHeaders() },
      )
      .pipe(this.withErrors('mise à jour routage atelier IA'));
  }

  adminAtlasPricingPatch(
    pricing: Record<string, number>,
  ): Observable<{ pricing_overrides: Record<string, number>; pricing: Record<string, number> }> {
    return this.http
      .patch<{ pricing_overrides: Record<string, number>; pricing: Record<string, number> }>(
        `${this.adminBase}/atlas/pricing`,
        { pricing },
        { headers: this.jsonHeaders() },
      )
      .pipe(this.withErrors('mise à jour tarifs atelier IA'));
  }

  // ── Shims ────────────────────────────────────────────────────────
  adminListShims(): Observable<{ shims?: ShimInfo[] } | ShimInfo[]> {
    return this.http
      .get<{ shims?: ShimInfo[] } | ShimInfo[]>(`${this.adminBase}/shims/`, {
        headers: this.authHeaders(),
      })
      .pipe(this.withErrors('chargement des shims'));
  }

  adminEnsureShim(vendorId: number): Observable<any> {
    return this.http
      .post(
        `${this.adminBase}/shims/${vendorId}/ensure`,
        {},
        { headers: this.jsonHeaders() },
      )
      .pipe(this.withErrors('wake-up du shim'));
  }

  adminScaleShim(vendorId: number, replicas: 0 | 1): Observable<any> {
    return this.http
      .post(
        `${this.adminBase}/shims/${vendorId}/scale`,
        { replicas },
        { headers: this.jsonHeaders() },
      )
      .pipe(this.withErrors('scale du shim'));
  }

  adminDeleteShim(vendorId: number): Observable<any> {
    return this.http
      .delete(`${this.adminBase}/shims/${vendorId}`, {
        headers: this.authHeaders(),
      })
      .pipe(this.withErrors('tear-down du shim'));
  }

  // ── Workspaces ────────────────────────────────────────────────────
  adminListWorkspaces(): Observable<{ provisioned_vendor_ids: number[] }> {
    return this.http
      .get<{ provisioned_vendor_ids: number[] }>(
        `${this.adminBase}/workspaces/`,
        { headers: this.authHeaders() },
      )
      .pipe(this.withErrors('chargement des workspaces'));
  }

  adminProvisionWorkspace(vendorId: number): Observable<any> {
    return this.http
      .post(
        `${this.adminBase}/workspaces/${vendorId}/provision`,
        {},
        { headers: this.jsonHeaders() },
      )
      .pipe(this.withErrors('provisioning du workspace'));
  }

  adminSyncWorkspaces(): Observable<any> {
    return this.http
      .post(`${this.adminBase}/workspaces/sync`, {}, { headers: this.jsonHeaders() })
      .pipe(this.withErrors('sync des workspaces'));
  }

  // ── Security overview ────────────────────────────────────────────
  adminSecurityOverview(): Observable<SecurityOverview> {
    return this.http
      .get<SecurityOverview>(`${this.adminBase}/security/overview`, {
        headers: this.authHeaders(),
      })
      .pipe(this.withErrors('chargement du dashboard sécurité'));
  }

  // ── Catalogue (réutilise endpoint existant /sites/ai/provider/models) ──
  listProviderModels(): Observable<{
    providers: Array<{
      id: string;
      label: string;
      models: Array<{ id: string; label: string; tier: string }>;
    }>;
  }> {
    return this.http
      .get<any>(`${this.cfg.apiUrl}/sites/ai/provider/models`, {
        headers: this.authHeaders(),
      })
      .pipe(this.withErrors('chargement du catalogue de modèles'));
  }
}
